/* eslint-disable jsx-a11y/anchor-is-valid */
import { AxiosError } from "axios";
import moment from "moment";
import qs from "qs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useLocation } from "react-router-dom";
import { getCommonFacilityListAsync } from "src/api/building/building-api";
import { CommonFacilityModel } from "src/api/building/building-types";
import { useApiOperation } from "src/api/hooks";
import { editPriceRulesAsync, getPriceRulesListAsync, postPriceRulesAsync } from "src/api/price/price-api";
import { PriceRules, PriceRulesModel } from "src/api/price/price-types";
import { getProviderList } from "src/api/provider/provider-api";
import { ProviderModel } from "src/api/provider/provider-types";
import { Modal } from "src/api/public-types";
import { BaseButton, BaseCheckbox, BaseInput, BaseModal, BaseRadio, BaseSelect, BaseTooltip } from "src/components";
import BaseChip from "src/components/BaseChip";
import BaseNewTabLink from "src/components/BaseNewTabLink";
import CustomTimePicker from "src/components/CustomTimePicker";
import EditCancelButton from "src/components/EditCancelButton";
import RangeDatepicker from "src/components/RangeDatepicker";
import BaseSectionTitle from "src/components/layout/BaseSectionTitle";
import MetaTag from "src/components/layout/MetaTag";
import useNavigate from "src/hooks/usePartnerNavigate";
import { PagePath } from "src/pages/product/details";
import { useToast } from "src/recoil/toast/hook";
import { YmdFormat, onlyNumber } from "src/utils";
import FacilitySelectModal from "../../components/FacilitySelectModal";
import ProviderSelectModal from "../../components/ProviderSelectModal";
import { weekDays } from "../../price-types";

/**
 * feature:: rangeDataPicker에서 1개의 react-hook-form 컨트롤러를 사용하므로
 * 상세 데이터 호출 시 displayDate를 setValue 해줌. 타입은 dateRange state와 같다.
 */
type SelectedProvider = {
  providerId: string;
  providerName: string;
};
type SelectedFacility = {
  buildingCommonFacilityId: string;
  facilityName: string;
  buildingName: string;
  buildingId: string;
};

const ruleTypeOption = {
  PRICE_RULE_BASIC: "PRICE_RULE_BASIC",
  PRICE_RULE_SPECIFIC: "PRICE_RULE_SPECIFIC",
} as const;

const ruleTypeOptions = [
  { value: "PRICE_RULE_BASIC", label: "기본 요금" },
  { value: "PRICE_RULE_SPECIFIC", label: "개별 요금" },
];

const BasicInfoForm = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const queryParams = useMemo(
    () =>
      qs.parse(location.search, {
        ignoreQueryPrefix: true,
        allowDots: true,
      }),
    [location],
  );

  const id: string | undefined = queryParams?.id ? String(queryParams.id) : undefined;

  const [selectedRuleType, setSelectedRuleType] = useState("");
  const [selectedRulesOption, setSelectedRulesOption] = useState<string>();

  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);
  const [price, setPrice] = useState<PriceRulesModel>();
  const [selectedProvider, setSelectedProvider] = useState<SelectedProvider[]>([]);
  const [selectedFacility, setSelectedFacility] = useState<SelectedFacility[]>([]);

  const [isProviderSelectModalOpen, setIsProviderSelectModalOpen] = useState<Modal>({ isOpen: false });
  const [isFacilitySelectModalOpen, setIsFacilitySelectModalOpen] = useState<Modal>({ isOpen: false });
  const [confirmModal, setConfirmModal] = useState<Modal>({ isOpen: false });
  const [errorModal, setErrorModal] = useState<Modal>({ isOpen: false });

  // 등록 실패 응답의 errorCode 에 기반해 안내문구를 재정의 하므로 공통 에러 모달을 쓰지 않는다
  const { executeAsync: postPriceRules } = useApiOperation(postPriceRulesAsync, { noErrorModal: true });
  const { executeAsync: editPriceRules } = useApiOperation(editPriceRulesAsync);
  const { executeAsync: getPriceRulesDetail } = useApiOperation(getPriceRulesListAsync);
  const { executeAsync: getProvider } = useApiOperation(getProviderList);
  const { executeAsync: getCommonFacilityList } = useApiOperation(getCommonFacilityListAsync);

  const { openToast } = useToast();
  const defaultValues: any = useMemo(() => {
    return {
      providerId: "",
      name: "",
      priceRuleType: "",
      buildingCommonFacilityId: "",
      startDate: "",
      endDate: "",
      startTime: "",
      endTime: "",
      cost: "",
      price: "",
      selectedDays: weekDays.map((day) => day.value) as string[],
    };
  }, []);

  const {
    register,
    control,
    handleSubmit,
    getValues,
    setValue,
    setError,
    watch,
    unregister,
    trigger,
    clearErrors,
    reset,
    formState: { dirtyFields, errors },
  } = useForm<any>({
    defaultValues,
  });

  // register
  useEffect(() => {
    const requiredMessage = "필수 입력 항목입니다";
    if (selectedRuleType === "PRICE_RULE_BASIC" && getValues("priceRuleType") === PriceRules.PRICE_RULE_PROVIDER) {
      // 전체 공용공간 선택 일 때,
      unregister(["buildingCommonFacilityId", "startDate", "endDate", "startTime", "endTime"]);
    } else if (selectedRuleType === "PRICE_RULE_BASIC" && getValues("priceRuleType") === PriceRules.PRICE_RULE_FACILITY) {
      // 특정 공용공간 선택 일 때,
      // const nameValue = getValues("name");
      unregister(["startDate", "endDate", "startTime", "endTime"]);
      // setValue("name", nameValue);
      register("buildingCommonFacilityId", {
        required: requiredMessage,
      });
    } else {
      unregister(["buildingCommonFacilityId"]);

      register("startDate", {
        required: requiredMessage,
      });
      register("endDate", {
        required: requiredMessage,
      });
      register("startTime", {
        required: requiredMessage,
      });
      register("endTime", {
        required: requiredMessage,
      });
    }
    register("name", {
      required: requiredMessage,
    });
    register("providerId", {
      required: requiredMessage,
    });
    register("cost", {
      required: requiredMessage,
    });
    register("price", {
      required: requiredMessage,
    });
  }, [register, selectedRuleType, selectedRulesOption, unregister, getValues, setValue]);

  const handleAddPrice = async (payload: Parameters<typeof postPriceRules>[0]) => {
    try {
      await postPriceRules(payload);
    } catch (error) {
      const errorCode = error instanceof AxiosError ? error.response?.data?.meta?.errorCode : undefined;

      if (errorCode === "ePC0513") {
        setErrorModal({ isOpen: true, title: "이미 등록된 프로바이더의 기본 요금입니다" });
      } else if (errorCode === "ePC0516") {
        setErrorModal({
          isOpen: true,
          title: "프로바이더를 다시 확인해주세요",
          message: "프로바이더 목록에서 [관리처]는 공용공간 요금을 설정할 수 없습니다",
        });
      } else {
        setErrorModal({ isOpen: true, title: "잘못된 요청입니다", type: errorCode });
      }
      return;
    }

    openToast({ content: "정상적으로 등록되었습니다." });
    navigate(PagePath.price.list);
  };

  // fetch
  useEffect(() => {
    if (id) {
      const fetchApi = async () => {
        const response = await getPriceRulesDetail({ priceRuleIdList: id, page: 0, size: 1 });
        if (response.data.data.content.length > 0) {
          let priceData = response.data.data.content[0];
          let facilityData: CommonFacilityModel = {};

          const providerData: ProviderModel =
            (await getProvider({ providerId: priceData?.providerId?.toString() })
              .then((providerResponse) => providerResponse.data.data.content[0])
              .catch(() => undefined)) || {};

          if (providerData.providerId) {
            setSelectedProvider([{ providerId: String(providerData.providerId), providerName: String(providerData.providerName) }]);
          }

          if (priceData.buildingCommonFacilityId) {
            facilityData =
              (await getCommonFacilityList({
                searchType: "ID",
                keywordList: priceData?.buildingCommonFacilityId?.toString(),
              })
                .then((buildingFacilityResponse) => buildingFacilityResponse.data.data.content[0])
                .catch(() => undefined)) || {};

            if (facilityData.id) {
              setSelectedFacility([
                {
                  buildingCommonFacilityId: String(facilityData.id) || "",
                  facilityName: String(facilityData.facilityName) || "",
                  buildingName: String(facilityData.buildingName) || "",
                  buildingId: String(facilityData.buildingId) || "",
                },
              ]);
            }
          }

          const priceDetail = {
            ...priceData,
            providerName: providerData?.providerName,
            buildingId: facilityData?.buildingId,
            buildingName: facilityData?.buildingName,
            facilityName: facilityData?.facilityName,
          };

          setPrice(priceDetail);

          if (priceDetail.priceRuleType === PriceRules.PRICE_RULE_SPECIFIC) {
            setSelectedRuleType("PRICE_RULE_SPECIFIC");
          } else {
            setSelectedRuleType("PRICE_RULE_BASIC");
          }

          if (priceDetail.startDate && priceDetail.endDate) {
            const start = priceDetail.startDate ? moment(priceDetail.startDate).toDate() : null;
            const end = priceDetail.endDate ? moment(priceDetail.endDate).toDate() : null;
            setDateRange([start, end]);
          }

          reset({
            providerId: priceDetail.providerId,
            name: priceDetail.name,
            priceRuleType: priceDetail.priceRuleType,
            buildingCommonFacilityId: priceDetail.buildingCommonFacilityId,
            startDate: priceDetail.startDate ? moment(priceDetail.startDate).format(YmdFormat.WITH_TIME_ZONE) : "",
            endDate: priceDetail.endDate ? moment(priceDetail.endDate).format(YmdFormat.WITH_TIME_ZONE) : "",
            startTime: priceDetail.startTime,
            endTime: priceDetail.endTime,
            // endTime: priceDetail.endTime ? moment(priceDetail.endTime, "hh:mm").toDate() : "",
            cost: priceDetail.cost,
            price: priceDetail.price,
            selectedDays: (priceDetail as any).dayOfWeekList || [],
          });
          setValue("startTime", priceDetail.startTime);
          setValue("endTime", priceDetail.endTime);
          // 프로바이더, 공용공간 팝업 선택되어있으면 추가
        }
      };
      fetchApi();
    }
  }, [id, getCommonFacilityList, getProvider, getPriceRulesDetail, setValue, reset]);

  const onSubmit = useCallback(
    async (data: Partial<PriceRulesModel> & { selectedDays?: string[] }, e?: any) => {
      e.preventDefault();
      let payload: Partial<PriceRulesModel> = { ...data };

      // selectedDays를 dayOfWeekList로 변환
      if (data.selectedDays) {
        (payload as any).dayOfWeekList = data.selectedDays;
        delete (payload as any).selectedDays;
      }

      if (id) {
        let newPayload: any = {};
        for (const key in payload) {
          if (key !== "name") {
            if (payload[key as keyof PriceRulesModel]) {
              newPayload[key] = payload[key as keyof PriceRulesModel];
            }
          }
          if (price?.name !== payload["name"]) {
            newPayload = { ...newPayload, name: payload["name"] };
          }
        }
        // dayOfWeekList 추가
        if ((payload as any).dayOfWeekList) {
          newPayload.dayOfWeekList = (payload as any).dayOfWeekList;
        }
        setConfirmModal({ isOpen: true, message: "수정하시겠습니까?", type: "editPrice", payload: newPayload });
      } else {
        let newPayload: any = {};
        for (const key in payload) {
          if (payload[key as keyof PriceRulesModel]) {
            newPayload[key] = payload[key as keyof PriceRulesModel];
          }
        }
        // dayOfWeekList 추가
        if ((payload as any).dayOfWeekList) {
          newPayload.dayOfWeekList = (payload as any).dayOfWeekList;
        }
        setConfirmModal({ isOpen: true, message: "저장하시겠습니까?", type: "addPrice", payload: newPayload });
      }

      e.target.reset();
    },
    [id, price],
  );

  const onError = (errors: any, e?: any) => {
    e.preventDefault();
    // validation 걸렸을때
    return false;
  };

  const onAddedProviderSelectModal = (provider: SelectedProvider[]) => {
    if (!provider) return;
    setSelectedProvider(provider);
    setValue("providerId", provider[0].providerId, { shouldDirty: true });
    setIsProviderSelectModalOpen({ isOpen: false });
    trigger("providerId");
  };

  const onAddedFacilitySelectModal = (facility: SelectedFacility[]) => {
    if (!facility) return;
    setSelectedFacility(facility);
    setValue("buildingCommonFacilityId", facility[0].buildingCommonFacilityId, { shouldDirty: true });
    setIsFacilitySelectModalOpen({ isOpen: false });
    trigger("buildingCommonFacilityId");
  };
  const handleDeleteProvider = (id: string) => {
    const filteredDeleted = selectedProvider.filter((item) => item.providerId !== id);
    setValue("providerId", "");
    setSelectedProvider(filteredDeleted);
    trigger("providerId");
  };
  const handleDeleteFacility = (id: string) => {
    const filteredDeleted = selectedFacility.filter((item) => item.buildingCommonFacilityId !== id);
    setValue("buildingCommonFacilityId", "");
    setSelectedFacility(filteredDeleted);
    trigger("buildingCommonFacilityId");
  };

  const handleOnDateRangeChange = (dateRange: [Date | null, Date | null]) => {
    const [startDate, endDate] = dateRange;
    setDateRange(dateRange);

    if (!startDate || !endDate) {
      setValue("startDate", "", { shouldDirty: true, shouldValidate: true });
      setValue("endDate", "", { shouldDirty: true, shouldValidate: true });
      return;
    }

    let start = moment(startDate).format(YmdFormat.YYYY_MM_DD) + "T00:00:00.000+09:00";
    let end = moment(endDate).format(YmdFormat.YYYY_MM_DD) + "T23:59:59.999+09:00";

    setValue("startDate", start, { shouldDirty: true, shouldValidate: true });
    setValue("endDate", end, { shouldDirty: true, shouldValidate: true });
  };

  const isAnythingChanged = () => {
    if (id) {
      // TODO:: 변경사항 비교 수정
    } else {
      let changed: boolean = false;
      for (let key in defaultValues) {
        if (key !== "priceRuleType") {
          if (getValues(key)) {
            changed = true;
          }
        }
      }
      return changed;
    }
  };
  const handleChangeRuleType = (value: string) => {
    setSelectedRuleType(value);
    if (value === "PRICE_RULE_BASIC") {
      setValue("priceRuleType", "PRICE_RULE_PROVIDER");
    } else setValue("priceRuleType", value);
  };

  return (
    <>
      {id ? <MetaTag subTitle={price?.name || ""} /> : <MetaTag isCreatePage />}
      <form onSubmit={handleSubmit(onSubmit, onError)}>
        <div className="contents-container__scroll">
          <div className="contents-container__wrap price-page">
            <article className="contents-container__wrap-article">
              <BaseSectionTitle title={"기본정보"} noHashLink />
              <section className="contents-container__grid">
                <div className="contents-container__grid-index">
                  <div className="flex-center">
                    <p className="required">요금 유형</p>
                  </div>
                </div>
                <div className="contents-container__grid-contents">
                  <div className="flex-row flex-center-start">
                    <BaseSelect
                      placeholder="선택해주세요"
                      className="minmax200"
                      stateOptions={ruleTypeOptions}
                      value={selectedRuleType}
                      setStateValue={handleChangeRuleType}
                      isDisabled={id ? true : false}
                    />
                  </div>
                </div>
              </section>

              {(selectedRuleType === ruleTypeOption.PRICE_RULE_BASIC || selectedRuleType === ruleTypeOption.PRICE_RULE_SPECIFIC) && (
                <section className="contents-container__grid">
                  <div className="contents-container__grid-index">
                    <div className="flex-center">
                      <p className="required">프로바이더</p>
                    </div>
                  </div>
                  <div className="contents-container__grid-contents">
                    <div className="flex-row flex-center-start">
                      <BaseButton
                        type="button"
                        className="color-white size-medium"
                        title="선택"
                        disabled={id ? true : false}
                        onClick={() => setIsProviderSelectModalOpen({ isOpen: true })}
                      />
                      <div className="flex-files ml10">
                        {selectedProvider?.map((item) => (
                          <div className="flex-files__wrap" key={item.providerId}>
                            <BaseNewTabLink
                              path={`${PagePath.provider.detail.replace(":id", `${item.providerId}`)}`}
                              value={item.providerName || "-"}
                            />
                            {!id && <div className="delete-btn-x" onClick={() => handleDeleteProvider(item.providerId)}></div>}
                          </div>
                        ))}
                      </div>
                    </div>
                    {errors && errors.providerId && <p className="validation-text">{errors.providerId.message?.toString()}</p>}
                  </div>
                </section>
              )}
              {selectedRuleType === ruleTypeOption.PRICE_RULE_BASIC && (
                <section className="contents-container__grid">
                  <div className="contents-container__grid-index">
                    <p className="required">적용 대상</p>
                  </div>
                  <div className="contents-container__grid-contents">
                    <div className="flex-start-center">
                      <Controller
                        control={control}
                        name="priceRuleType"
                        render={({ field: { onChange, value, name } }) => (
                          <>
                            <div className="flex-center">
                              <BaseRadio
                                name={name}
                                id={PriceRules.PRICE_RULE_PROVIDER}
                                value={PriceRules.PRICE_RULE_PROVIDER}
                                checked={value === PriceRules.PRICE_RULE_PROVIDER ? true : false}
                                disabled={id ? true : false}
                                onChange={(_, e) => {
                                  onChange(PriceRules.PRICE_RULE_PROVIDER);
                                  setSelectedRulesOption(PriceRules.PRICE_RULE_PROVIDER);
                                  setValue("buildingCommonFacilityId", "");
                                  setSelectedFacility([]);
                                }}
                                label={"전체 공용공간"}
                                className="mr4"
                              />
                              <BaseTooltip
                                contents={""}
                                className="mr20"
                                tooltip={"프로바이더에 연결된 모든 상품에 공용공간 요금이 적용됩니다."}
                                touchIcon={"QUESTION"}
                              />
                              <BaseRadio
                                name={name}
                                id={PriceRules.PRICE_RULE_FACILITY}
                                value={PriceRules.PRICE_RULE_FACILITY}
                                checked={value === PriceRules.PRICE_RULE_FACILITY ? true : false}
                                disabled={id ? true : false}
                                onChange={(_, e) => {
                                  onChange(PriceRules.PRICE_RULE_FACILITY);
                                  setSelectedRulesOption(PriceRules.PRICE_RULE_FACILITY);
                                }}
                                label={"특정 공용공간"}
                              />
                              <BaseTooltip contents={""} className="mr20" tooltip={"선택한 공용공간에만 요금이 적용됩니다."} touchIcon={"QUESTION"} />
                              <BaseButton
                                type="button"
                                className="color-white size-medium"
                                title="선택"
                                disabled={watch("priceRuleType") !== PriceRules.PRICE_RULE_FACILITY}
                                onClick={() => setIsFacilitySelectModalOpen({ isOpen: true })}
                              />
                              <div className={`flex-files ml10`}>
                                {selectedFacility?.map((item) => (
                                  <div className="two-depth-link flex-center" key={item.buildingCommonFacilityId}>
                                    <BaseNewTabLink
                                      path={`${PagePath.building.detail.replace(":id", `${item.buildingId}`)}`}
                                      value={item.buildingName || "-"}
                                      isAddChevronPositionRight
                                    />
                                    <BaseNewTabLink
                                      path={`${PagePath.building.detail.replace(":id", `${item.buildingId}`)}?tab=facility`}
                                      value={item.facilityName || "-"}
                                    />
                                    <div className="delete-x-btn" onClick={() => handleDeleteFacility(item.buildingCommonFacilityId)}></div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </>
                        )}
                      ></Controller>
                    </div>
                    {errors && errors.buildingCommonFacilityId && (
                      <p className="validation-text">{errors.buildingCommonFacilityId.message?.toString()}</p>
                    )}
                  </div>
                </section>
              )}
              {(selectedRuleType === ruleTypeOption.PRICE_RULE_BASIC || selectedRuleType === ruleTypeOption.PRICE_RULE_SPECIFIC) && (
                <section className="contents-container__grid">
                  <div className="contents-container__grid-index">
                    <p className={`required`}>요금 정책명</p>
                  </div>

                  <div className="contents-container__grid-contents">
                    <div className="minmax240">
                      <Controller
                        control={control}
                        name="name"
                        render={({ field: { onChange, value, name, ref }, fieldState: { error } }) => (
                          <BaseInput
                            inputRef={ref}
                            placeholder="요금정책명을 입력해주세요"
                            className="mr8"
                            type="text"
                            onChange={onChange}
                            value={value}
                            name={name}
                            errorText={error?.message}
                          />
                        )}
                      ></Controller>
                    </div>
                  </div>
                </section>
              )}
              {selectedRuleType === ruleTypeOption.PRICE_RULE_SPECIFIC && (
                <>
                  <div className="flex-start custom-time-wrap">
                    <section className="contents-container__grid">
                      <div className="contents-container__grid-index">
                        <p className={"required"}>적용 기간</p>
                      </div>
                      <div className="contents-container__grid-contents">
                        <div className="flex-row price-select-weekday">
                          <div>
                            <div className="minmax240">
                              <RangeDatepicker
                                minDate={moment().toDate()}
                                dateRange={dateRange}
                                onChange={handleOnDateRangeChange}
                                onClearClick={() => handleOnDateRangeChange([null, null])}
                              />
                            </div>
                            {errors && errors.startDate && !errors.selectedDays && (
                              <p className="validation-text">{errors.startDate.message?.toString()}</p>
                            )}
                            {errors && errors.startDate && errors.selectedDays && (
                              <p className="validation-text">{errors.startDate.message?.toString()}</p>
                            )}
                            {errors && !errors.startDate && errors.selectedDays && (
                              <p className="validation-text">{errors.selectedDays.message?.toString()}</p>
                            )}
                          </div>
                          <div className="ml20 flex-center-start pt6">
                            <Controller
                              control={control}
                              name="selectedDays"
                              rules={{
                                validate: (value) => {
                                  if (selectedRuleType === ruleTypeOption.PRICE_RULE_SPECIFIC) {
                                    if (!value || value.length === 0) {
                                      return "최소 1개의 요일을 선택해주세요.";
                                    }
                                  }
                                  return true;
                                },
                              }}
                              render={({ field: { onChange, value }, fieldState: { error } }) => {
                                const selectedDaysArray = value || [];
                                const allDaysSelected = selectedDaysArray.length === 7;

                                const handleSelectAllDays = (checked: boolean) => {
                                  if (checked) {
                                    onChange(weekDays.map((day) => day.value));
                                  } else {
                                    onChange([]);
                                  }
                                };

                                const handleToggleDay = (day: string) => {
                                  const newArray = [...selectedDaysArray];
                                  const index = newArray.indexOf(day);
                                  if (index > -1) {
                                    newArray.splice(index, 1);
                                  } else {
                                    newArray.push(day);
                                  }
                                  onChange(newArray);
                                };

                                return (
                                  <>
                                    <div className="flex-row flex-center-start">
                                      <BaseCheckbox
                                        id="selectAllDays"
                                        name="selectAllDays"
                                        label="전체 요일"
                                        className="mr10"
                                        checked={allDaysSelected}
                                        onChange={handleSelectAllDays}
                                      />
                                    </div>
                                    <div className="flex-row flex-center-start gap4">
                                      {weekDays.map((day) => (
                                        <BaseChip
                                          key={day.value}
                                          title={day.label}
                                          selected={selectedDaysArray.includes(day.value)}
                                          onClick={() => handleToggleDay(day.value)}
                                          type="button"
                                          className="circle-chip"
                                        />
                                      ))}
                                    </div>
                                  </>
                                );
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </section>
                  </div>
                  <section className="contents-container__grid">
                    <div className="contents-container__grid-index">
                      <p className={`required`}>적용 시간</p>
                    </div>

                    <div className="contents-container__grid-contents">
                      <div className="flex-start flex-row minmax240">
                        <div className="w-100">
                          <Controller
                            control={control}
                            name="startTime"
                            render={({ field: { onChange, value, name }, fieldState: { error } }) => {
                              return (
                                <CustomTimePicker
                                  placeholder="시작 시간 선택"
                                  timeIntervals={30}
                                  setDate={onChange}
                                  className="price-time-picker"
                                  name={name}
                                  selectedTime={value}
                                  midnight
                                />
                              );
                            }}
                          ></Controller>
                        </div>
                        <div className="py10">
                          <span className="mx10">{`~`}</span>
                        </div>
                        <div className="w-100">
                          <Controller
                            control={control}
                            name="endTime"
                            render={({ field: { onChange, value, name }, fieldState: { error } }) => {
                              return (
                                <CustomTimePicker
                                  placeholder="종료 시간 선택"
                                  timeIntervals={30}
                                  setDate={onChange}
                                  className="price-time-picker"
                                  name={name}
                                  minTime={watch("startTime")}
                                  selectedTime={value}
                                  midnight
                                />
                              );
                            }}
                          ></Controller>
                        </div>
                      </div>
                      {errors && (errors.startTime || errors.endTime) && <p className="validation-text">필수입력 항목입니다</p>}
                    </div>
                  </section>
                </>
              )}
              {(selectedRuleType === ruleTypeOption.PRICE_RULE_BASIC || selectedRuleType === ruleTypeOption.PRICE_RULE_SPECIFIC) && (
                <div className="flex-center">
                  <section className="contents-container__grid">
                    <div className="contents-container__grid-index">
                      <p className={`required`}>원가 (원/30분)</p>
                    </div>
                    <div className="contents-container__grid-contents">
                      <div className="minmax240">
                        <Controller
                          control={control}
                          name="cost"
                          render={({ field: { onChange, value, name, ref }, fieldState: { error } }) => (
                            <BaseInput
                              inputRef={ref}
                              placeholder="원가를 입력해주세요"
                              maxLength={9}
                              errorText={error?.message}
                              type="text"
                              name={name}
                              onChange={onChange}
                              className="mr8"
                              value={onlyNumber(String(value))}
                            />
                          )}
                        ></Controller>
                      </div>
                    </div>
                  </section>
                  <section className="contents-container__grid flex-start">
                    <div className="contents-container__grid-index flex-center">
                      <p className={`required no-wrap`}>판매가 (원/30분)</p>{" "}
                      <BaseTooltip contents={""} className="ml4 mr20" tooltip={"0원을 입력하면 '무료'로 표시됩니다."} touchIcon={"QUESTION"} />
                    </div>

                    <div className="contents-container__grid-contents">
                      <div className="minmax240">
                        <Controller
                          control={control}
                          name="price"
                          render={({ field: { onChange, value, name, ref }, fieldState: { error } }) => (
                            <BaseInput
                              inputRef={ref}
                              placeholder="판매가를 입력해주세요"
                              className="mr8"
                              type="text"
                              onChange={onChange}
                              value={onlyNumber(String(value))}
                              name={name}
                              maxLength={9}
                              errorText={error?.message}
                            />
                          )}
                        ></Controller>
                      </div>
                    </div>
                  </section>
                </div>
              )}
            </article>
          </div>
        </div>

        {/* 버튼영역 */}
        <div className="contents-container__btn-wrap">
          <div className="left-area"></div>
          <div className="right-area">
            {Number(id) > 0 ? (
              // <BaseButton
              //   title="수정 취소"
              //   className="size-large color-white"
              //   onClick={() => {
              //     if (isAnythingChanged()) {
              //       setConfirmModal({ isOpen: true, type: "checkChanged", message: "현재 페이지에서 나가시겠습니까?" });
              //     } else {
              //       navigate(PagePath.price.detail.replace(":id", String(id)));
              //     }
              //   }}
              // />
              <EditCancelButton to={PagePath.price.detail.replace(":id", String(id))} />
            ) : (
              <BaseButton
                title="취소"
                className="size-large color-white"
                onClick={() => {
                  if (isAnythingChanged()) {
                    setConfirmModal({ isOpen: true, type: "checkChanged", message: "현재 페이지에서 나가시겠습니까?" });
                  } else {
                    navigate(PagePath.price.list);
                  }
                }}
              />
            )}
            <BaseButton title="저장" type={"submit"} disabled={!selectedRuleType} className="size-large ml10" />
          </div>
        </div>
      </form>
      {isProviderSelectModalOpen.isOpen && (
        <ProviderSelectModal
          onCanceled={() => setIsProviderSelectModalOpen({ isOpen: false })}
          onAdded={onAddedProviderSelectModal}
          defaultChecked={selectedProvider ? selectedProvider : []}
        />
      )}
      {isFacilitySelectModalOpen.isOpen && (
        <FacilitySelectModal
          onCanceled={() => setIsFacilitySelectModalOpen({ isOpen: false })}
          onAdded={onAddedFacilitySelectModal}
          defaultChecked={selectedFacility ? selectedFacility : []}
        />
      )}
      {confirmModal.isOpen && (
        <BaseModal
          title={confirmModal.message}
          isOpen={confirmModal.isOpen}
          btnLeftTitle={confirmModal.type === "addPrice" || confirmModal.type === "editPrice" ? "취소" : "머무르기"}
          btnRightTitle={confirmModal.type === "addPrice" || confirmModal.type === "editPrice" ? "확인" : "나가기"}
          onClick={async () => {
            if (confirmModal.type === "addPrice") {
              setConfirmModal({ isOpen: false });
              await handleAddPrice(confirmModal.payload);
            } else if (confirmModal.type === "editPrice") {
              setConfirmModal({ isOpen: false });
              const { data } = await editPriceRules({ id: Number(id), payload: confirmModal.payload || {} });
              if (data.data) {
                openToast({ content: "정상적으로 수정되었습니다." });
                navigate(`${PagePath.price.detail.replace(":id", String(id))}`);
              }
            } else if (confirmModal.type === "checkChanged") {
              if (id) {
                setConfirmModal({ isOpen: false });
                navigate(`${PagePath.price.detail.replace(":id", String(id))}`);
              } else {
                setConfirmModal({ isOpen: false });
                navigate(`${PagePath.price.list}`);
              }
            }
          }}
          onClose={() => setConfirmModal({ isOpen: false })}
        >
          {confirmModal.type === "checkChanged" && (
            <div>
              <span>
                입력 중인 내용이 있는 경우,
                <br /> 저장을 하지 않은 정보는 사라집니다.
              </span>
            </div>
          )}
        </BaseModal>
      )}
      {errorModal.isOpen && (
        <BaseModal
          isOpen={true}
          title={errorModal.title}
          btnRightTitle={"확인"}
          onClick={() => {
            setErrorModal({ isOpen: false });
          }}
        >
          {errorModal.type ? (
            <div>
              <span>{`[${confirmModal.type}]`}</span>
            </div>
          ) : errorModal.message ? (
            <div>
              <span>{errorModal.message}</span>
            </div>
          ) : null}
        </BaseModal>
      )}
    </>
  );
};

export default BasicInfoForm;
